<template>
  <div class="indicator-config">
    <div class="config-header">
      <h5>{{ t('simulation.indicatorConfig.title') }}</h5>
      <button @click="$emit('close')" class="close-btn">×</button>
    </div>
    
    <div class="config-content">
      <!-- 快速预设 -->
      <div class="preset-section">
        <h6>{{ t('simulation.indicatorConfig.presets.title') }}</h6>
        <div class="preset-buttons">
          <button 
            v-for="preset in presets" 
            :key="preset.key"
            @click="applyPreset(preset)"
            class="preset-btn"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>

      <!-- 指标分类选择 -->
      <div class="categories-section">
        <div v-for="category in indicatorCategories" :key="category.name" class="category">
          <div class="category-header">
            <h6>{{ category.name }}</h6>
            <button 
              @click="toggleCategory(category)"
              class="toggle-all-btn"
            >
              {{ isCategoryFullySelected(category) ? t('simulation.indicatorConfig.actions.deselectAll') : t('simulation.indicatorConfig.actions.selectAll') }}
            </button>
          </div>
          
          <div class="indicator-grid">
            <div 
              v-for="indicator in category.indicators" 
              :key="indicator.key"
              class="indicator-card"
              :class="{ selected: selectedIndicators.includes(indicator.key) }"
            >
              <label class="indicator-label">
                <input 
                  type="checkbox" 
                  :value="indicator.key"
                  v-model="selectedIndicators"
                  @change="$emit('selection-change', selectedIndicators)"
                />
                <div class="indicator-info">
                  <span class="indicator-name">{{ indicator.name }}</span>
                  <small class="indicator-desc">{{ indicator.description }}</small>
                  <div v-if="indicator.params" class="indicator-params">
                    <span v-for="param in indicator.params" :key="param" class="param-tag">
                      {{ param }}
                    </span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- 显示设置 -->
      <div class="display-settings">
        <h6>{{ t('simulation.indicatorConfig.display.title') }}</h6>
        <div class="setting-row">
          <label>
            <input 
              type="checkbox" 
              v-model="settings.syncTimeAxis"
              @change="$emit('settings-change', settings)"
            />
            {{ t('simulation.indicatorConfig.display.syncTimeAxis') }}
          </label>
        </div>
        <div class="setting-row">
          <label>
            <input 
              type="checkbox" 
              v-model="settings.showDataLabels"
              @change="$emit('settings-change', settings)"
            />
            {{ t('simulation.indicatorConfig.display.showDataLabels') }}
          </label>
        </div>
        <div class="setting-row">
          <label>{{ t('simulation.indicatorConfig.display.chartHeight') }}</label>
          <select 
            v-model="settings.chartHeight"
            @change="$emit('settings-change', settings)"
          >
            <option value="200">{{ t('simulation.indicatorConfig.display.heightOption', { label: t('simulation.indicatorConfig.display.size.small'), value: 200 }) }}</option>
            <option value="300">{{ t('simulation.indicatorConfig.display.heightOption', { label: t('simulation.indicatorConfig.display.size.medium'), value: 300 }) }}</option>
            <option value="400">{{ t('simulation.indicatorConfig.display.heightOption', { label: t('simulation.indicatorConfig.display.size.large'), value: 400 }) }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="config-footer">
      <button @click="resetToDefault" class="btn btn-secondary">{{ t('simulation.indicatorConfig.actions.reset') }}</button>
      <button @click="$emit('close')" class="btn btn-primary">{{ t('common.confirm') }}</button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  indicatorCategories: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'selection-change', 'settings-change', 'close'])

const { t } = useI18n()

const selectedIndicators = ref([...props.modelValue]);
const settings = ref({
  syncTimeAxis: true,
  showDataLabels: false,
  chartHeight: 300
});

// 预设配置
const PRESET_DEFINITIONS = [
  {
    key: 'basic',
    indicators: ['macd', 'rsi', 'volume']
  },
  {
    key: 'trend',
    indicators: ['macd', 'bollinger', 'adx']
  },
  {
    key: 'oscillation',
    indicators: ['rsi', 'aroon', 'cmf']
  },
  {
    key: 'volume',
    indicators: ['volume', 'obv', 'cmf']
  },
  {
    key: 'comprehensive',
    indicators: ['macd', 'rsi', 'bollinger', 'adx', 'aroon', 'obv', 'cmf', 'volume']
  }
]

const presets = computed(() =>
  PRESET_DEFINITIONS.map((preset) => ({
    ...preset,
    label: t(`simulation.indicatorConfig.presets.items.${preset.key}`)
  }))
)

// 计算属性：检查分类是否完全选中
const isCategoryFullySelected = (category) => {
  return category.indicators.every(indicator => 
    selectedIndicators.value.includes(indicator.key)
  );
};

// 方法
const applyPreset = (preset) => {
  selectedIndicators.value = [...preset.indicators];
  emit('selection-change', selectedIndicators.value);
  emit('update:modelValue', selectedIndicators.value);
};

const toggleCategory = (category) => {
  const isFullySelected = isCategoryFullySelected(category);
  
  if (isFullySelected) {
    // 取消选择该分类的所有指标
    category.indicators.forEach(indicator => {
      const index = selectedIndicators.value.indexOf(indicator.key);
      if (index > -1) {
        selectedIndicators.value.splice(index, 1);
      }
    });
  } else {
    // 选择该分类的所有指标
    category.indicators.forEach(indicator => {
      if (!selectedIndicators.value.includes(indicator.key)) {
        selectedIndicators.value.push(indicator.key);
      }
    });
  }
  
  emit('selection-change', selectedIndicators.value);
  emit('update:modelValue', selectedIndicators.value);
};

const resetToDefault = () => {
  selectedIndicators.value = ['macd', 'rsi'];
  settings.value = {
    syncTimeAxis: true,
    showDataLabels: false,
    chartHeight: 300
  };
  emit('selection-change', selectedIndicators.value);
  emit('settings-change', settings.value);
  emit('update:modelValue', selectedIndicators.value);
};
</script>

<style scoped>
.indicator-config {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}

.config-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #8c8c8c;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #262626;
}

.config-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.preset-section {
  margin-bottom: 24px;
}

.preset-section h6 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.preset-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.preset-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.categories-section {
  margin-bottom: 24px;
}

.category {
  margin-bottom: 20px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.category-header h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
}

.toggle-all-btn {
  background: none;
  border: 1px solid #d9d9d9;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  color: #666;
  transition: all 0.2s;
}

.toggle-all-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.indicator-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.indicator-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 12px;
  transition: all 0.2s;
  cursor: pointer;
}

.indicator-card:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 4px rgba(24,144,255,0.1);
}

.indicator-card.selected {
  border-color: #1890ff;
  background: #f0f8ff;
}

.indicator-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  width: 100%;
}

.indicator-label input[type="checkbox"] {
  margin: 2px 0 0 0;
  flex-shrink: 0;
}

.indicator-info {
  flex: 1;
}

.indicator-name {
  font-weight: 500;
  color: #262626;
  font-size: 13px;
  display: block;
  margin-bottom: 4px;
}

.indicator-desc {
  color: #8c8c8c;
  font-size: 11px;
  display: block;
  line-height: 1.4;
}

.indicator-params {
  margin-top: 6px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.param-tag {
  background: #f0f0f0;
  color: #666;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
}

.display-settings h6 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.setting-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
}

.setting-row label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.setting-row select {
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #e8e8e8;
  background: #fafafa;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-secondary {
  background: white;
  border-color: #d9d9d9;
  color: #666;
}

.btn-secondary:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-primary {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
}

.btn-primary:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

@media (max-width: 768px) {
  .indicator-config {
    max-width: 95vw;
    max-height: 90vh;
  }
  
  .indicator-grid {
    grid-template-columns: 1fr;
  }
  
  .preset-buttons {
    flex-direction: column;
  }
  
  .preset-btn {
    width: 100%;
  }
}
</style>
